import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent } from './ui/dialog';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, CreditCard, X, Target, Users, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { trackPurchase } from '../lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Monthly subscription types eligible for autopay (forced ON)
const AUTOPAY_ELIGIBLE_TYPES = ['options_only', 'complete_package', 'equity_only', 'index_options_educational'];

const PaymentModal = ({ isOpen, onClose, subscriptionType, packageDetails, appliedCoupon: preAppliedCoupon }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    user_telegram_id: '',
    service_start_date: ''
  });

  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Referral and Points state
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [userPoints, setUserPoints] = useState(0);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  
  // Autopay state — forced ON for monthly subscriptions (no toggle)
  const isAutopayEligible = AUTOPAY_ELIGIBLE_TYPES.includes(subscriptionType);
  const autopayEnabled = isAutopayEligible; // always on when eligible

  // Check if this is an automated alert subscription
  const isAutomatedAlert = subscriptionType?.includes('automated_alerts') || false;

  // Fetch user's points balance when email changes
  useEffect(() => {
    if (formData.user_email) {
      fetchUserPoints(formData.user_email);
    }
  }, [formData.user_email]);

  const fetchUserPoints = async (email) => {
    try {
      const response = await fetch(`${API}/referral/user/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setUserPoints(data.points_balance || 0);
      }
    } catch (error) {
      console.log('Error fetching points:', error);
    }
  };

  // Auto-apply coupon if provided from offer
  useEffect(() => {
    if (preAppliedCoupon && isOpen) {
      setCouponCode(preAppliedCoupon);
      // Auto-validate the coupon
      setTimeout(() => {
        autoApplyCoupon(preAppliedCoupon);
      }, 500);
    }
  }, [preAppliedCoupon, isOpen]);

  const autoApplyCoupon = async (code) => {
    if (!code) return;
    
    try {
      const response = await fetch(`${API}/coupons/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: code.toUpperCase(),
          subscription_type: subscriptionType,
          user_email: 'auto@apply.com', // Temporary for validation
          original_amount: packageDetails?.amount || 0
        })
      });
      
      const result = await response.json();
      
      if (result.valid) {
        setAppliedCoupon({
          coupon_code: code.toUpperCase(),
          coupon_id: result.coupon_id
        });
        setCouponDiscount(result.discount_amount);
        setCouponError('');
        toast({
          title: "🎁 Offer Applied!",
          description: `Discount of ₹${result.discount_amount} applied automatically`,
        });
      }
    } catch (error) {
      console.log('Auto-apply coupon failed:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'user_phone') {
      // Digits only; allow an optional leading 0 by stripping it; cap at 10 digits.
      let digits = value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 10);
      setFormData(prev => ({ ...prev, user_phone: digits }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    try {
      const response = await fetch(`${API}/coupons/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: couponCode,
          subscription_type: subscriptionType,
          user_email: formData.user_email,
          original_amount: packageDetails.amount
        })
      });

      const result = await response.json();
      
      if (result.valid) {
        // Preserve the entered code so the UI shows ✓ Coupon "MAY10" applied (not empty quotes)
        setAppliedCoupon({
          ...result,
          coupon_code: couponCode.trim().toUpperCase()
        });
        setCouponDiscount(result.discount_amount);
        setCouponError('');
        toast({
          title: "Coupon Applied!",
          description: `You saved ₹${result.discount_amount}`,
        });
      } else {
        setCouponError(result.message);
        setAppliedCoupon(null);
        setCouponDiscount(0);
      }
    } catch (error) {
      setCouponError('Failed to apply coupon');
      console.error('Coupon error:', error);
    }
  };

  const validateReferralCode = async () => {
    if (!referralCode.trim()) return;
    
    try {
      const response = await fetch(`${API}/referral/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referral_code: referralCode })
      });
      
      const result = await response.json();
      
      if (result.valid) {
        setReferralValid(true);
        setReferrerName(result.referrer_name || '');
        setReferralError('');
        toast({
          title: "Referral Code Valid!",
          description: `Referred by ${result.referrer_name}`,
        });
      } else {
        setReferralValid(false);
        setReferrerName('');
        setReferralError(result.message || 'Invalid referral code');
      }
    } catch (error) {
      setReferralError('Failed to validate code');
      console.error('Referral error:', error);
    }
  };

  const applyPoints = async () => {
    if (pointsToUse <= 0 || appliedCoupon) return;
    
    const MIN_POINTS = 250;
    if (pointsToUse < MIN_POINTS) {
      toast({
        title: "Minimum Points Required",
        description: `Minimum ${MIN_POINTS} points required to redeem`,
        variant: "destructive"
      });
      return;
    }
    
    if (pointsToUse > userPoints) {
      toast({
        title: "Insufficient Points",
        description: `You have only ${userPoints} points available`,
        variant: "destructive"
      });
      return;
    }
    
    // 1 point = ₹1
    const discount = Math.min(pointsToUse, packageDetails?.amount || 0);
    setPointsDiscount(discount);
    setCouponDiscount(0);  // Clear any coupon discount
    setAppliedCoupon(null);
    
    toast({
      title: "Points Applied!",
      description: `₹${discount} discount using ${discount} points`,
    });
  };

  const removePoints = () => {
    setPointsDiscount(0);
    setPointsToUse(0);
  };

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponError('');
  };

  const validateForm = () => {
    const { user_name, user_email, user_phone, user_telegram_id } = formData;
    
    if (!user_name.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter your name",
        variant: "destructive",
      });
      return false;
    }
    
    if (!user_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user_email)) {
      toast({
        title: "Validation Error", 
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return false;
    }
    
    if (!user_phone.trim() || !/^[6-9]\d{9}$/.test(user_phone.replace(/\D/g, '').replace(/^0+/, ''))) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid 10-digit mobile number",
        variant: "destructive",
      });
      return false;
    }
    
    // Telegram username is required (the @ sign is optional — we accept both).
    if (!user_telegram_id || !user_telegram_id.trim()) {
      toast({
        title: "Telegram ID Required",
        description: "Please enter your Telegram username to receive trading signals (e.g. yourname or @yourname).",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      // Enhanced loading with timeout for mobile devices
      const timeout = setTimeout(() => {
        console.error('Razorpay script load timeout');
        resolve(false);
      }, 10000); // 10 second timeout
      
      script.onload = () => {
        clearTimeout(timeout);
        // Additional check for mobile devices
        if (window.Razorpay) {
          resolve(true);
        } else {
          setTimeout(() => {
            resolve(window.Razorpay ? true : false);
          }, 500);
        }
      };
      
      script.onerror = () => {
        clearTimeout(timeout);
        console.error('Failed to load Razorpay script');
        resolve(false);
      };
      
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Mobile device detection
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Load Razorpay script with timeout for mobile
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast({
          title: "Error",
          description: "Failed to load payment gateway. Please check your internet connection and try again.",
          variant: "destructive",
        });
        return;
      }

      // Add mobile-specific delay to ensure script is fully loaded
      if (isMobile) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Determine API endpoint based on subscription type and autopay
      const useAutopay = isAutopayEligible && autopayEnabled && !isAutomatedAlert;

      if (useAutopay) {
        // === AUTOPAY FLOW (Razorpay Subscriptions) ===
        const autopayPayload = {
          user_name: formData.user_name,
          user_email: formData.user_email,
          user_phone: formData.user_phone,
          user_telegram_id: formData.user_telegram_id,
          subscription_type: subscriptionType,
          service_start_date: formData.service_start_date || new Date().toISOString().split('T')[0]
        };

        if (appliedCoupon && appliedCoupon.valid && couponDiscount > 0) {
          autopayPayload.coupon_code = couponCode;
          autopayPayload.final_amount = (packageDetails?.amount || 0) - couponDiscount - pointsDiscount;
        }
        if (referralValid && referralCode.trim()) {
          autopayPayload.referral_code = referralCode.toUpperCase();
        }
        if (pointsDiscount > 0 && !appliedCoupon) {
          autopayPayload.points_to_use = pointsDiscount;
        }

        const autopayRes = await fetch(`${API}/autopay/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(autopayPayload)
        });

        if (!autopayRes.ok) {
          const errData = await autopayRes.json();
          throw new Error(errData.detail || errData.message || 'Failed to create autopay subscription');
        }

        const autopayData = await autopayRes.json();

        // HYBRID MODE: First discounted payment as regular order, then autopay
        if (autopayData.payment_mode === 'order_then_autopay') {
          try {
            const options = {
              key: autopayData.razorpay_key_id,
              order_id: autopayData.razorpay_order_id,
              amount: Math.round(autopayData.amount * 100),
              currency: 'INR',
              name: 'Upper Circuit Stocks',
              description: `${packageDetails?.name || subscriptionType} (First Month - Discounted)`,
              prefill: {
                name: formData.user_name,
                email: formData.user_email,
                contact: formData.user_phone,
              },
              theme: { color: '#D4AF37' },
              handler: async (response) => {
                try {
                  const verifyRes = await fetch(`${API}/autopay/verify-first-payment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      razorpay_payment_id: response.razorpay_payment_id,
                      razorpay_order_id: response.razorpay_order_id,
                      razorpay_signature: response.razorpay_signature,
                      record_id: autopayData.record_id,
                    })
                  });
                  const result = await verifyRes.json();
                  if (verifyRes.ok) {
                    const thankYouParams = new URLSearchParams({
                      telegram_link: result.telegram_group_link || '',
                      package: packageDetails?.name || 'Subscription',
                      amount: String(autopayData.amount),
                      name: formData.user_name,
                      email: formData.user_email,
                      subscription_id: result.subscription_id || '',
                      referral_code: '',
                      autopay: 'true'
                    });
                    trackPurchase({ value: autopayData.amount });
                    onClose();
                    navigate(`/login?${thankYouParams.toString()}&paid=true`);
                  } else {
                    toast({ title: 'Payment verification failed', description: result.detail || result.message || 'Please contact support.', variant: 'destructive' });
                    setIsLoading(false);
                  }
                } catch (err) {
                  toast({ title: 'Payment verification failed', description: err.message || 'Please try again.', variant: 'destructive' });
                  setIsLoading(false);
                }
              },
              modal: { ondismiss: () => { setIsLoading(false); } }
            };
            
            if (!window.Razorpay) {
              throw new Error('Razorpay SDK not loaded. Please refresh the page and try again.');
            }
            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (resp) => {
              toast({ title: 'Payment failed', description: resp.error?.description || 'Please try again.', variant: 'destructive' });
              setIsLoading(false);
            });
            rzp.open();
          } catch (rzpError) {
            console.error('Razorpay checkout error:', rzpError);
            toast({ title: 'Payment error', description: rzpError.message || 'Failed to open payment page. Please try again.', variant: 'destructive' });
            setIsLoading(false);
          }
          return;
        }

        // STANDARD AUTOPAY: Razorpay Subscription checkout
        const options = {
          key: autopayData.razorpay_key_id,
          subscription_id: autopayData.razorpay_subscription_id,
          name: 'Upper Circuit Stocks',
          description: `${packageDetails?.name || subscriptionType} - Monthly Auto-Renewal`,
          prefill: {
            name: formData.user_name,
            email: formData.user_email,
            contact: formData.user_phone,
          },
          theme: { color: '#D4AF37' },
          handler: async (response) => {
            try {
              // For subscriptions, Razorpay sends subscription_id in the response
              const verifyRes = await fetch(`${API}/autopay/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_signature: response.razorpay_signature,
                })
              });
              const result = await verifyRes.json();
              if (verifyRes.ok) {
                const totalDiscount = couponDiscount + pointsDiscount;
                const amountPaid = (packageDetails?.amount || 0) - totalDiscount;
                const thankYouParams = new URLSearchParams({
                  telegram_link: result.telegram_group_link || '',
                  package: packageDetails?.name || 'Subscription',
                  amount: String(amountPaid),
                  name: formData.user_name,
                  email: formData.user_email,
                  subscription_id: result.subscription_id || '',
                  pending: result.pending ? 'true' : 'false',
                  start_date: result.service_start_date || '',
                  referral_code: result.user_referral_code || '',
                  autopay: 'true'
                });
                trackPurchase({ value: amountPaid });
                onClose();
                navigate(`/login?${thankYouParams.toString()}&paid=true`);
              } else {
                throw new Error(result.message || 'Payment verification failed');
              }
            } catch (error) {
              console.error('Autopay verification error:', error);
              toast({
                title: "Verification Error",
                description: "Payment processed but verification failed. Contact support.",
                variant: "destructive",
              });
            }
          },
          modal: {
            ondismiss: () => {
              setIsLoading(false);
              toast({
                title: "Payment Cancelled",
                description: "Subscription setup was cancelled.",
                variant: "destructive",
              });
            },
            escape: true,
            backdropclose: false,
          },
        };

        try {
          const razorpay = new window.Razorpay(options);
          razorpay.on('payment.failed', function (response) {
            console.error('Payment failed:', response.error);
            toast({
              title: "Payment Failed",
              description: `${response.error.description}. Please try again.`,
              variant: "destructive",
            });
            setIsLoading(false);
          });
          razorpay.open();
        } catch (razorpayError) {
          console.error('Razorpay init error:', razorpayError);
          toast({
            title: "Payment Gateway Error",
            description: "Unable to initialize payment. Please refresh and try again.",
            variant: "destructive",
          });
          setIsLoading(false);
        }

      } else {
        // === ONE-TIME PAYMENT FLOW (existing) ===
        const apiEndpoint = isAutomatedAlert ?
          `${API}/automated-alerts/create` :
          `${API}/subscriptions/create`;

      // Create subscription with coupon/referral/points if applied
      const subscriptionPayload = {
        ...formData,
        subscription_type: subscriptionType
      };
      
      // Add coupon code if applied
      if (appliedCoupon && appliedCoupon.valid && couponDiscount > 0) {
        subscriptionPayload.coupon_code = couponCode;
      }
      
      // Add referral code if valid
      if (referralValid && referralCode.trim()) {
        subscriptionPayload.referral_code = referralCode.toUpperCase();
      }
      
      // Add points to use (only if no coupon applied)
      if (pointsDiscount > 0 && !appliedCoupon) {
        subscriptionPayload.points_to_use = pointsDiscount;
      }
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionPayload),
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const subscriptionData = await response.json();

      // Configure Razorpay options with mobile optimizations
      const options = {
        key: subscriptionData.razorpay_key_id,
        amount: subscriptionData.amount * 100, // Convert to paise
        currency: subscriptionData.currency,
        name: 'Upper Circuit Stocks',
        description: `${packageDetails.name} - ${isAutomatedAlert ? 'Trading Days' : 'Monthly'} Subscription`,
        order_id: subscriptionData.order_id,
        prefill: {
          name: formData.user_name,
          email: formData.user_email,
          contact: formData.user_phone,
        },
        theme: {
          color: '#D4AF37',
        },
        // Mobile optimizations
        config: {
          display: {
            blocks: {
              utib: { // UPI block
                name: 'Pay using UPI',
                instruments: [
                  { method: 'upi' }
                ]
              },
              other: { // Other payment methods
                name: 'Other Payment Methods',
                instruments: [
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'wallet' }
                ]
              }
            },
            sequence: ['block.utib', 'block.other'],
            preferences: {
              show_default_blocks: true
            }
          }
        },
        // Enhanced mobile error handling
        handler: async (response) => {
          try {
            await verifyPayment(response, isAutomatedAlert);
          } catch (error) {
            console.error('Payment verification error:', error);
            toast({
              title: "Payment Verification Error",
              description: "Payment was processed but verification failed. Please contact support with your payment ID: " + (response.razorpay_payment_id || 'N/A'),
              variant: "destructive",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setIsLoading(false);
            toast({
              title: "Payment Cancelled",
              description: "Payment was cancelled. You can try again.",
              variant: "destructive",
            });
          },
          // Mobile-specific modal settings
          escape: true,
          backdropclose: false,
          handleback: true,
        },
        // Add retry mechanism for mobile
        retry: {
          enabled: true,
          max_count: 3
        }
      };

      // Open Razorpay checkout with error handling
      try {
        const razorpay = new window.Razorpay(options);
        
        // Add error event listener for mobile issues
        razorpay.on('payment.failed', function (response) {
          console.error('Payment failed:', response.error);
          toast({
            title: "Payment Failed",
            description: `Payment failed: ${response.error.description}. Please try again or contact support.`,
            variant: "destructive",
          });
          setIsLoading(false);
        });

        razorpay.open();
      } catch (razorpayError) {
        console.error('Razorpay initialization error:', razorpayError);
        toast({
          title: "Payment Gateway Error",
          description: "Unable to initialize payment gateway. Please refresh the page and try again.",
          variant: "destructive",
        });
        setIsLoading(false);
      }

      } // end else (one-time payment flow)

    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Error",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPayment = async (razorpayResponse, isAutomatedAlert = false) => {
    try {
      const verifyEndpoint = isAutomatedAlert ? 
        `${API}/automated-alerts/verify` : 
        `${API}/payments/verify`;

      const verifyResponse = await fetch(verifyEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
        }),
      });

      const result = await verifyResponse.json();

      if (verifyResponse.ok) {
        console.log('Payment verification successful:', result);
        
        // Calculate actual amount paid (after discounts)
        const totalDiscount = couponDiscount + pointsDiscount;
        const amountPaid = (packageDetails?.amount || 0) - totalDiscount;
        
        // Build query params for Thank You page
        const thankYouParams = new URLSearchParams({
          telegram_link: result.telegram_group_link || '',
          package: packageDetails?.name || 'Subscription',
          amount: String(amountPaid),
          name: formData.user_name,
          email: formData.user_email,
          subscription_id: result.subscription_id || '',
          pending: result.pending ? 'true' : 'false',
          start_date: result.service_start_date || '',
          referral_code: result.user_referral_code || ''
        });

        // Close modal and redirect to Login page (with Telegram join button)
        trackPurchase({ value: amountPaid });
        onClose();
        navigate(`/login?${thankYouParams.toString()}&paid=true`);
        
      } else {
        throw new Error(result.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast({
        title: "Verification Error",
        description: "Payment was processed but verification failed. Please contact support.",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  const getPackageIcon = () => {
    switch (subscriptionType) {
      case 'options_only':
        return <Target className="w-6 h-6 text-golden" />;
      case 'complete_package':
        return <CreditCard className="w-6 h-6 text-golden" />;
      case 'equity_only':
        return <Users className="w-6 h-6 text-golden" />;
      default:
        return <CreditCard className="w-6 h-6 text-golden" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md bg-gray-900 border-golden/20 text-white p-4 max-h-[75vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold text-golden">Subscribe - ₹{packageDetails?.amount}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-3">
          {/* Compact Form */}
          <div className="space-y-2">
            <Input
              name="user_name"
              value={formData.user_name}
              onChange={handleInputChange}
              placeholder="Full Name *"
              className="bg-black/50 border-golden/20 text-white h-9"
              required
            />
            <Input
              name="user_email"
              type="email"
              value={formData.user_email}
              onChange={handleInputChange}
              placeholder="Email Address *"
              className="bg-black/50 border-golden/20 text-white h-9"
              required
            />
            <Input
              name="user_phone"
              type="tel"
              value={formData.user_phone}
              onChange={handleInputChange}
              placeholder="WhatsApp Number *"
              className="bg-black/50 border-golden/20 text-white h-9"
              required
            />
            <Input
              name="user_telegram_id"
              value={formData.user_telegram_id}
              onChange={handleInputChange}
              placeholder="Telegram Username * (e.g. yourname — @ optional)"
              className="bg-black/50 border-golden/20 text-white h-9"
              required
            />
            <div className="flex items-center justify-between -mt-1">
              <p className="text-xs text-amber-400">⚠️ Required to receive trading signals on Telegram</p>
              <a
                href="https://t.me/UPPERCIRCUITSTOCKS_Bot?start=subscribe"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.1.154.234.17.331.015.098.034.322.019.496z"/>
                </svg>
                Enable Notifications
              </a>
            </div>
            
            {/* Service Start Date */}
            {!isAutomatedAlert && (
              <div className="bg-amber-900/20 border border-amber-600/30 rounded p-2 text-xs">
                <label className="text-amber-400 block mb-1">When do you want to start the service? *</label>
                <Input
                  type="date"
                  name="service_start_date"
                  value={formData.service_start_date || ''}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-black/50 border-golden/20 text-white h-9"
                  required
                />
                <p className="text-amber-300 mt-1">Your subscription will be valid for 30 days from the start date</p>
              </div>
            )}
            
            {/* Auto-renewal is mandatory internally for monthly subscriptions —
                we intentionally do NOT show any banner/toggle to the subscriber.
                autopayEnabled is forced ON via the AUTOPAY_ELIGIBLE_TYPES list. */}
            
            {/* Coupon Code Input */}
            <div className="flex space-x-2">
              <Input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Coupon Code (optional)"
                className="bg-black/50 border-golden/20 text-white h-9 flex-1"
              />
              <Button
                type="button"
                onClick={applyCoupon}
                className="bg-golden/20 text-golden border border-golden/40 hover:bg-golden/30 h-9 px-3"
                disabled={!couponCode.trim()}
              >
                Apply
              </Button>
            </div>
            
            {/* Coupon Status */}
            {appliedCoupon && (
              <div className="bg-green-900/50 border border-green-600/30 rounded p-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-green-400">✓ Coupon &quot;{appliedCoupon.coupon_code}&quot; applied</span>
                  <button onClick={removeCoupon} className="text-red-400 hover:text-red-300">×</button>
                </div>
              </div>
            )}
            
            {couponError && (
              <div className="bg-red-900/50 border border-red-600/30 rounded p-2 text-xs text-red-400">
                {couponError}
              </div>
            )}
          </div>

          {/* Workshop Benefits */}
          {subscriptionType === 'workshop' && (
            <div className="bg-black/30 rounded p-2 text-xs">
              <p className="text-golden font-semibold mb-1">Includes:</p>
              <p className="text-gray-300">• Intraday Options Setup • Entry/Exit Signals • Scanner • Videos • Indicators</p>
            </div>
          )}

          {/* Referral Code Section */}
          <div className="bg-black/30 rounded p-2">
            <Label className="text-gray-300 text-xs">Have a Referral Code?</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Enter referral code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                disabled={referralValid}
                className="bg-black/50 border-gray-600 text-white text-sm h-8"
              />
              <Button 
                type="button" 
                size="sm"
                onClick={validateReferralCode}
                disabled={referralValid || !referralCode.trim()}
                className="bg-gray-700 hover:bg-gray-600 h-8"
              >
                {referralValid ? '✓' : 'Apply'}
              </Button>
            </div>
            {referralValid && (
              <p className="text-xs text-green-400 mt-1">✓ Referred by {referrerName}</p>
            )}
            {referralError && (
              <p className="text-xs text-red-400 mt-1">{referralError}</p>
            )}
          </div>

          {/* Points Section */}
          {userPoints > 0 && !appliedCoupon && (
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded p-2 border border-purple-500/30">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-purple-300 text-xs font-semibold">🎁 Your Points Balance</Label>
                <span className="text-purple-400 font-bold">{userPoints} pts</span>
              </div>
              {pointsDiscount === 0 ? (
                <>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Points to use"
                      value={pointsToUse || ''}
                      onChange={(e) => setPointsToUse(Math.min(Number(e.target.value), userPoints))}
                      max={userPoints}
                      min={0}
                      className="bg-black/50 border-gray-600 text-white text-sm h-8"
                    />
                    <Button 
                      type="button" 
                      size="sm"
                      onClick={applyPoints}
                      disabled={pointsToUse < 250}
                      className="bg-purple-600 hover:bg-purple-500 h-8"
                    >
                      Use Points
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Min 250 points • 1 point = ₹1</p>
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-green-400 text-sm">₹{pointsDiscount} discount applied!</span>
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={removePoints}
                    variant="ghost"
                    className="text-red-400 h-6 text-xs"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Payment Amount Display */}
          {(couponDiscount > 0 || pointsDiscount > 0) && (
            <div className="bg-black/30 rounded p-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-300">Original Amount:</span>
                <span className="text-gray-300 line-through">₹{packageDetails?.amount}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-green-400">Coupon Discount:</span>
                  <span className="text-green-400">-₹{couponDiscount}</span>
                </div>
              )}
              {pointsDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-purple-400">Points Discount:</span>
                  <span className="text-purple-400">-₹{pointsDiscount}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t border-gray-600 pt-1 mt-1">
                <span className="text-golden">Final Amount:</span>
                <span className="text-golden">₹{packageDetails?.amount - couponDiscount - pointsDiscount}</span>
              </div>
            </div>
          )}

          {/* Points + Coupon Warning */}
          {appliedCoupon && userPoints > 0 && (
            <p className="text-xs text-yellow-400 bg-yellow-900/20 p-2 rounded">
              ⚠️ Points cannot be used with coupon codes. Remove coupon to use points.
            </p>
          )}

          {/* Payment Button */}
          <Button
            onClick={handlePayment}
            disabled={isLoading}
            className="w-full bg-golden text-black hover:bg-golden-light py-2 font-semibold"
            data-testid="payment-submit-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {isAutopayEligible && autopayEnabled ? (
                  <><RefreshCw className="mr-2 h-4 w-4" />
                  Subscribe ₹{(packageDetails?.amount - couponDiscount - pointsDiscount) || packageDetails?.amount}/month</>
                ) : (
                  <><CreditCard className="mr-2 h-4 w-4" />
                  Pay ₹{(packageDetails?.amount - couponDiscount - pointsDiscount) || packageDetails?.amount} & Subscribe</>
                )}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;